import type { ComponentType } from 'react';

/**
 * Wraps a dynamic import with a single-retry mechanism.
 * After a deploy, cached HTML may reference old chunk hashes that no longer
 * exist on the server. The browser returns an HTML error page instead of JS,
 * causing "… is not a valid JavaScript MIME type" errors.
 *
 * On first failure we set a sessionStorage flag and reload the page so the
 * browser fetches fresh HTML with updated chunk URLs. If the flag is already
 * set (meaning the reload didn't help), we let the error propagate to the
 * ErrorBoundary.
 */
export function lazyRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): () => Promise<{ default: T }> {
  return async () => {
    const key = 'lazyRetry_reloaded';
    try {
      return await importFn();
    } catch (error) {
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        // Return a never-resolving promise so React doesn't render stale content
        return new Promise(() => {});
      }
      // Already reloaded once — clear the flag and let ErrorBoundary handle it
      sessionStorage.removeItem(key);
      throw error;
    }
  };
}
